// Nguồn hàng & trạng thái căn: nhãn, tiến độ thanh toán / mốc hoa hồng mặc định, trạng thái hiệu lực của căn
// (giữ quá hạn tự trở về trống, cửa sổ ưu tiên hàng chờ) và điều kiện giữ căn.
import type { ErrorCode, Grant } from '@cathouse/contracts';
import type { Rules } from './config.js';
import { MIN } from './dates.js';
import { fmtTime } from './format.js';
import type { Id, Milestone, MilestoneType, PaymentPlanStep, Project, ProjectStatus, Timestamp, Unit, UnitLock, UnitStatus } from './types.js';

export const UNIT_STATUS: Readonly<Record<UnitStatus, { label: string; short: string }>> = {
  open: { label: 'Còn trống', short: 'Trống' },
  locked: { label: 'Đang giữ', short: 'Giữ' },
  pending: { label: 'Chờ duyệt cọc', short: 'Chờ duyệt' },
  deposited: { label: 'Đã cọc', short: 'Cọc' },
  contracted: { label: 'Đã ký HĐ', short: 'Đã bán' },
  blocked: { label: 'Chưa mở bán', short: 'Khoá' },
};
export const PROJECT_TYPES: readonly string[] = ['Căn hộ', 'Nhà phố', 'Biệt thự', 'Shophouse', 'Đất nền', 'Condotel'];
export const PROJECT_STATUS: Readonly<Record<ProjectStatus, string>> = { upcoming: 'Sắp mở bán', selling: 'Đang mở bán', paused: 'Tạm dừng', closed: 'Đã đóng giỏ' };
export const LEGAL_TYPES: readonly string[] = ['Sổ hồng riêng', 'Sổ đỏ', 'HĐMB với CĐT', 'Đang chờ sổ', 'Giấy phép xây dựng', 'Văn bản đủ điều kiện bán nhà hình thành trong tương lai'];
export const MILESTONE_TYPES: Readonly<Record<MilestoneType, string>> = {
  deposit: 'Xác nhận cọc',
  contract: 'Ký HĐMB/HĐĐC',
  paidPct: 'KH thanh toán ≥ X%',
  devPaid: 'CĐT đã trả phí cho sàn',
};
/** Tiến độ thanh toán mặc định khi dự án chưa khai báo (tổng 100%). */
export const DEFAULT_PLAN: readonly PaymentPlanStep[] = [
  { label: 'Đợt 1 — ký HĐMB (trừ tiền cọc)', pct: 30, days: 15 },
  { label: 'Đợt 2', pct: 20, days: 120 },
  { label: 'Đợt 3 — nhận nhà', pct: 45, days: 365 },
  { label: 'Đợt 4 — nhận sổ', pct: 5, days: 540 },
];
/** Mốc đủ điều kiện chi hoa hồng mặc định: 50% khi ký HĐ, 50% khi khách thanh toán ≥ 30%. */
export const DEFAULT_MILESTONES: readonly Milestone[] = [
  { type: 'contract', pct: 50 },
  { type: 'paidPct', threshold: 30, pct: 50 },
];

// ---------- Trạng thái hiệu lực ----------
/** Phần trạng thái căn mà effectiveUnit cần. */
export interface UnitState {
  status?: UnitStatus;
  lock?: (Partial<UnitLock> & Pick<UnitLock, 'until'>) | null;
  releasedAt?: Timestamp | null;
  queue?: readonly Id[];
}
/** Cửa sổ ưu tiên: chỉ người đầu hàng chờ được giữ tới `until`. */
export interface PriorityWindow {
  staffId: Id;
  until: Timestamp;
}
/** Căn kèm trạng thái hiệu lực `st`; `lockLeft` (ms) khi đang giữ còn hạn; `priority` khi đang trong cửa sổ ưu tiên. */
export type EffectiveUnit<U extends UnitState = Unit> = U & {
  st: UnitStatus;
  lockLeft?: number;
  priority?: PriorityWindow;
};

/**
 * Trạng thái hiệu lực của căn tại `now`: giữ chỗ quá hạn (lock.until ≤ now) tự trở về "Còn trống" với mốc trống
 * = lock.until; căn trống có hàng chờ và trong [mốc trống, mốc trống + priorityMinutes) ⇒ ưu tiên queue[0].
 * Mốc trống của căn trống thường = releasedAt (không có ⇒ không có cửa sổ ưu tiên).
 */
export function effectiveUnit<U extends UnitState>(u: U, cfg: { rules: Pick<Rules, 'priorityMinutes'> }, now: Timestamp = Date.now()): EffectiveUnit<U> {
  let st: UnitStatus = u.status || 'open';
  let freeAt = u.releasedAt || 0;
  if (st === 'locked') {
    if (u.lock && u.lock.until > now) {
      return { ...u, st, lockLeft: u.lock.until - now };
    }
    st = 'open';
    freeAt = u.lock ? u.lock.until : freeAt;
  }
  const out: EffectiveUnit<U> = { ...u, st };
  if (st === 'open' && u.queue && u.queue.length && freeAt) {
    const until = freeAt + cfg.rules.priorityMinutes * MIN;
    if (now < until) out.priority = { staffId: u.queue[0] as Id, until };
  }
  return out;
}

// ---------- Điều kiện giữ căn ----------
/** Người muốn giữ căn: hồ sơ nhân sự (staffId) + có quyền `booking.lock` hay không. */
export interface LockViewer {
  staffId: Id | null;
  canLock: boolean;
}
/** Dựng LockViewer từ Actor (MessageContext.actor) hoặc chủ thể kiểm quyền bất kỳ. */
export const lockViewerOf = (actor: { staffId: Id | null; grants: Readonly<Record<string, Grant>> } | null | undefined): LockViewer | null =>
  actor ? { staffId: actor.staffId, canLock: !!actor.grants['booking.lock'] } : null;

export type LockBlockCode = 'NO_STAFF_PROFILE' | 'LOCK_PERMISSION_REQUIRED' | 'PROJECT_NOT_SELLING' | 'UNIT_NOT_AVAILABLE' | 'PRIORITY_RESERVED' | 'MAX_LOCKS_REACHED';
/** Lý do không giữ được căn: mã ổn định (booking-service R3) + thông điệp tiếng Việt như prototype. */
export interface LockBlock {
  code: LockBlockCode;
  message: string;
}

/** Mã lỗi ứng dụng (→ HTTP ở gateway) của từng lý do — booking-service §3.5: 403 / 422 / 409. */
export const LOCK_BLOCK_ERROR: Readonly<Record<LockBlockCode, ErrorCode>> = {
  NO_STAFF_PROFILE: 'FORBIDDEN',
  LOCK_PERMISSION_REQUIRED: 'FORBIDDEN',
  PROJECT_NOT_SELLING: 'INVARIANT',
  UNIT_NOT_AVAILABLE: 'CONFLICT',
  PRIORITY_RESERVED: 'CONFLICT',
  MAX_LOCKS_REACHED: 'INVARIANT',
};

/**
 * Kiểm điều kiện giữ căn theo đúng thứ tự prototype; null = được giữ. Quyền là tham số tường minh
 * (`viewer.canLock` ⇔ booking.lock) thay cho bộ phân giải quyền toàn cục của prototype.
 * @param eu trạng thái hiệu lực (effectiveUnit) tại thời điểm kiểm
 * @param myLockCount số căn người này đang giữ còn hạn
 */
export function lockBlocker(
  viewer: LockViewer | null | undefined,
  eu: Pick<EffectiveUnit, 'st' | 'priority'>,
  project: Pick<Project, 'status'> | null | undefined,
  myLockCount: number,
  cfg: { rules: Pick<Rules, 'maxLocks'> },
): LockBlock | null {
  if (!viewer || !viewer.staffId) return { code: 'NO_STAFF_PROFILE', message: 'Chưa đăng nhập hồ sơ nhân sự' };
  if (!viewer.canLock) return { code: 'LOCK_PERMISSION_REQUIRED', message: 'Vai trò của bạn không giữ căn' };
  if (!project || project.status !== 'selling') return { code: 'PROJECT_NOT_SELLING', message: 'Dự án chưa mở bán' };
  if (eu.st !== 'open') return { code: 'UNIT_NOT_AVAILABLE', message: `Căn đang ở trạng thái "${UNIT_STATUS[eu.st].label}"` };
  if (eu.priority && eu.priority.staffId !== viewer.staffId)
    return { code: 'PRIORITY_RESERVED', message: `Đang ưu tiên cho người xếp hàng đến ${fmtTime(eu.priority.until)}` };
  if (myLockCount >= cfg.rules.maxLocks)
    return { code: 'MAX_LOCKS_REACHED', message: `Bạn đang giữ ${myLockCount}/${cfg.rules.maxLocks} căn — chốt hoặc nhả bớt trước` };
  return null;
}

/** Đơn giá / m² (0 khi chưa có diện tích). */
export const pricePerM2 = (u: Pick<Unit, 'price' | 'area'>) => (u.area ? u.price / u.area : 0);
